import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Plus, Save, X } from 'lucide-react';
import { getHoldingProfile, saveHoldingProfile } from '../../../../../api/collections';
import { useCountry } from '../../../../../context/CountryContext';
import { showToast } from '../../../../../utils/showToast';
import { cn } from '../../../../../utils/cn';
import { Button } from '../../../../../components/ui/Button';
import { Input } from '../../../../../components/ui/Input';
import { Skeleton } from '../../../../../components/ui/Skeleton';
import { FieldLabel } from '../../../../../components/ui/FieldLabel';
import {
  emailToAddError,
  isContactEmailsRejection,
  normalizeEmail,
  MAX_CONTACT_PHONE_LENGTH,
} from '../../utils/commerceRuleForm';
import {
  buildProfilePayload,
  profileToForm,
  validateProfileForm,
  MAX_CONTACT_NAME_LENGTH,
} from '../../utils/holdingForms';
import { holdingErrorText, missingFieldsText } from '../../utils/holdings';
import { apiErrorMessage, apiErrorStatus } from '../../utils/collectionReminders';

/**
 * Perfil de cobro del holding (microservice-billing): contacto de facturación (nombre,
 * correos y teléfono) y plazo de pago, propio o heredado del operador. La primera emisión de
 * la nota consolidada exige todo, y el plazo ELEGIDO explícitamente: "heredar" también es una
 * elección.
 *
 * El teléfono viaja tal como se escribe: billing lo normaliza a E.164 con el país del
 * operador y devuelve el resultado. La nota del holding va a los correos de este perfil.
 *
 * @param {{ holdingId: string, onSaved?: (view: object) => void }} props
 */
export default function HoldingCollectionProfileForm({ holdingId, onSaved }) {
  const { t } = useTranslation();
  const baseId = useId();
  const { countryName, resolved: countryResolved } = useCountry();

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const applyView = useCallback((data) => {
    const next = profileToForm(data?.profile);
    setView(data);
    setInitial(next);
    setForm(next);
    setErrors({});
    setSaveError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await getHoldingProfile(holdingId);
      applyView(data);
    } catch (err) {
      setLoadError({ status: apiErrorStatus(err), message: apiErrorMessage(err), err });
    } finally {
      setLoading(false);
    }
  }, [holdingId, applyView]);

  useEffect(() => {
    if (!holdingId) return;
    setView(null);
    setForm(null);
    setEmailDraft('');
    setEmailError(null);
    load();
  }, [holdingId, load]);

  const payload = useMemo(
    () => (form && initial ? buildProfilePayload(form, initial) : {}),
    [form, initial],
  );
  const dirty = Object.keys(payload).length > 0 || emailDraft.trim() !== '';

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaveError(null);
  };

  // Agrega el correo escrito. Devuelve la lista resultante o null si no se pudo.
  const addDraftEmail = () => {
    const email = normalizeEmail(emailDraft);
    const problem = emailToAddError(email, form.contactEmails);
    if (problem) {
      setEmailError(problem);
      return null;
    }
    const next = [...form.contactEmails, email];
    update({ contactEmails: next });
    setEmailDraft('');
    setEmailError(null);
    return next;
  };

  const removeEmail = (email) =>
    update({ contactEmails: form.contactEmails.filter((e) => e !== email) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Un correo escrito y sin "Agregar" se perdería en silencio al guardar: se agrega (o se
    // frena el guardado si no es válido).
    let effectiveForm = form;
    if (emailDraft.trim() !== '') {
      const emails = addDraftEmail();
      if (!emails) return;
      effectiveForm = { ...form, contactEmails: emails };
    }
    const validation = validateProfileForm(effectiveForm);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const body = buildProfilePayload(effectiveForm, initial);
    if (Object.keys(body).length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await saveHoldingProfile(holdingId, body);
      applyView(data);
      showToast(t('Perfil de cobro del holding guardado.'), 'success');
      onSaved?.(data);
    } catch (err) {
      const text = isContactEmailsRejection(err)
        ? t('Algún correo de cobranza no es válido. Revisá la lista y volvé a guardar.')
        : holdingErrorText(err, t, 'No se pudo guardar el perfil de cobro del holding.');
      setSaveError(text);
      showToast(text, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !view) {
    return (
      <div className="space-y-3" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (loadError && !view) {
    const retryable = loadError.status !== 403 && loadError.status !== 404;
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error"
        role="alert"
      >
        <span>
          {holdingErrorText(loadError.err, t, 'No se pudo cargar el perfil de cobro del holding.')}
        </span>
        {retryable && (
          <Button size="sm" variant="outline" onClick={load}>
            {t('Reintentar')}
          </Button>
        )}
      </div>
    );
  }

  if (!form) return null;

  const missing = view?.missingFields ?? [];
  // Lo que heredaría hoy. Con plazo propio guardado billing no informa el del operador, así
  // que no se muestra nada en vez de adivinar.
  const effective = view?.effective ?? {};
  let inheritHint = null;
  if (effective.source === 'operator' && Number(effective.paymentTermDays) > 0) {
    inheritHint = t('Hoy: {{n}} días (plazo general del operador).', { n: effective.paymentTermDays });
  } else if (!effective.source) {
    inheritHint = t('El operador no tiene plazo general: sus notas no vencen.');
  }
  const ids = {
    name: `${baseId}-name`,
    term: `${baseId}-term`,
    email: `${baseId}-email`,
    phone: `${baseId}-phone`,
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit} noValidate>
      {missing.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-md bg-status-warning-soft px-2.5 py-1.5 text-xs text-status-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {t('Falta para emitir la nota del holding: {{fields}}.', {
              fields: missingFieldsText(missing, t),
            })}
          </span>
        </p>
      )}

      <div className="space-y-1">
        <FieldLabel htmlFor={ids.name}>{t('Contacto de facturación')}</FieldLabel>
        <Input
          id={ids.name}
          autoComplete="off"
          maxLength={MAX_CONTACT_NAME_LENGTH}
          value={form.contactName}
          invalid={!!errors.contactName}
          aria-describedby={errors.contactName ? `${ids.name}-error` : undefined}
          placeholder={t('Nombre y apellido')}
          onChange={(e) => update({ contactName: e.target.value })}
        />
        {errors.contactName && (
          <p id={`${ids.name}-error`} className="text-xs text-status-error">
            {t(errors.contactName)}
          </p>
        )}
      </div>

      {/* Correos de facturación: la nota del holding se envía a todos. */}
      <div className="space-y-2">
        <FieldLabel htmlFor={ids.email}>{t('Correos de facturación')}</FieldLabel>
        {form.contactEmails.length > 0 && (
          <ul className="flex flex-wrap gap-1.5" aria-label={t('Correos cargados')}>
            {form.contactEmails.map((email) => (
              <li
                key={email}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-0.5 pl-2.5 pr-1 text-xs text-brand-700"
              >
                {email}
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  aria-label={t('Quitar {{email}}', { email })}
                  className="rounded-full p-0.5 hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Input
            id={ids.email}
            type="email"
            autoComplete="off"
            placeholder="facturacion@holding.cl"
            value={emailDraft}
            invalid={!!emailError}
            aria-describedby={emailError ? `${ids.email}-error` : `${ids.email}-hint`}
            onChange={(e) => {
              setEmailDraft(e.target.value);
              setEmailError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDraftEmail();
              }
            }}
          />
          <Button
            variant="outline"
            onClick={addDraftEmail}
            iconLeft={<Plus className="h-4 w-4" />}
            disabled={!emailDraft.trim()}
          >
            {t('Agregar')}
          </Button>
        </div>
        {emailError ? (
          <p id={`${ids.email}-error`} className="text-xs text-status-error">
            {t(emailError)}
          </p>
        ) : (
          <p id={`${ids.email}-hint`} className="text-xs text-fg-muted">
            {t('La nota del holding se envía a estos correos, con el enlace al PDF.')}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <FieldLabel htmlFor={ids.phone}>{t('Teléfono de facturación')}</FieldLabel>
        <Input
          id={ids.phone}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          maxLength={MAX_CONTACT_PHONE_LENGTH}
          placeholder="+56 9 1234 5678"
          value={form.contactPhone}
          invalid={!!errors.contactPhone}
          aria-describedby={`${ids.phone}-hint`}
          onChange={(e) => update({ contactPhone: e.target.value })}
        />
        <p
          id={`${ids.phone}-hint`}
          className={cn('text-xs', errors.contactPhone ? 'text-status-error' : 'text-fg-muted')}
        >
          {errors.contactPhone
            ? t(errors.contactPhone)
            : countryResolved && countryName
              ? t('Sin código de país se toma como un número de {{pais}}.', { pais: countryName })
              : t('Sin código de país se toma con el país del operador.')}
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-fg">{t('Plazo de pago')}</legend>
        {!form.termMode && (
          <p className="text-xs text-status-warning">
            {t('Elegí si el holding hereda el plazo general o tiene uno propio: la primera nota lo exige.')}
          </p>
        )}
        <label className="flex items-start gap-2 text-sm text-fg cursor-pointer">
          <input
            type="radio"
            name={`${baseId}-term-mode`}
            className="mt-1 accent-brand-500"
            checked={form.termMode === 'inherit'}
            onChange={() => update({ termMode: 'inherit' })}
          />
          <span>
            {t('Hereda el plazo general del operador')}
            {inheritHint && <span className="block text-xs text-fg-muted">{inheritHint}</span>}
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-fg cursor-pointer">
          <input
            type="radio"
            name={`${baseId}-term-mode`}
            className="mt-1 accent-brand-500"
            checked={form.termMode === 'own'}
            onChange={() => update({ termMode: 'own' })}
          />
          <span>{t('Plazo propio para este holding')}</span>
        </label>
        {form.termMode === 'own' && (
          <div className="ml-6 flex items-center gap-2">
            <Input
              id={ids.term}
              type="number"
              inputMode="numeric"
              min={1}
              max={365}
              className="w-28"
              value={form.termDays}
              invalid={!!errors.termDays}
              aria-label={t('Días de plazo')}
              aria-describedby={errors.termDays ? `${ids.term}-error` : undefined}
              onChange={(e) => update({ termDays: e.target.value })}
            />
            <span className="text-sm text-fg-muted">{t('días corridos desde la emisión')}</span>
          </div>
        )}
        {errors.termDays && (
          <p id={`${ids.term}-error`} className="ml-6 text-xs text-status-error">
            {t(errors.termDays)}
          </p>
        )}
      </fieldset>

      {/* Decisión del 2026-09-25: los recordatorios de holdings son de una fase posterior. */}
      <p className="flex items-start gap-1.5 text-xs text-fg-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          {t('Los recordatorios de cobranza para holdings llegan próximamente: por ahora el seguimiento de sus notas es manual.')}
        </span>
      </p>

      {saveError && (
        <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setForm(initial);
            setEmailDraft('');
            setEmailError(null);
            setErrors({});
            setSaveError(null);
          }}
          disabled={!dirty || saving}
        >
          {t('Descartar cambios')}
        </Button>
        <Button type="submit" loading={saving} disabled={!dirty} iconLeft={<Save className="h-4 w-4" />}>
          {t('Guardar contacto y plazo')}
        </Button>
      </div>
    </form>
  );
}
